import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  getRenewalStatusDocuments,
  saveRenewalStatusDocuments,
  type RenewalStatusApplicationItem,
} from "../../api/renewal";
import { generateUploadPresignedUrls } from "../../api/discussion";
import { parseFilingApiDateString, toLocalYmd } from "../../utils/filingApiDates";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const RENEWAL_STATUS_DOCUMENTS_FORM_ID = "renewal-status-documents-form";

const ACCEPTED_TYPES = ".pdf";
const ACCEPTED_HELP = "PDF format, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const UPLOAD_FOLDER_PREFIX = "renewal/status";

const LABEL_STYLE =
  "text-xs font-normal leading-snug text-[#222222] w-full flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

type PdfSlot = "renewalIntimation" | "statusOfApplication";

type ApplicationRowState = RenewalStatusApplicationItem & {
  pendingRenewalIntimationFilename?: string | null;
  pendingStatusOfApplicationFilename?: string | null;
};

type PreviewState = {
  url: string;
  name: string;
  kind: FilePreviewKind;
};

type RenewalStatusDocumentsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function dateValue(value: string | null | undefined): Date | null {
  return parseFilingApiDateString(value ?? null);
}

function toIsoDate(value: string | null | undefined): string | null {
  const d = parseFilingApiDateString(value ?? null);
  return d ? toLocalYmd(d) : null;
}

export default function RenewalStatusDocumentsView({
  leadId,
  onSuccess,
}: RenewalStatusDocumentsViewProps) {
  const [applications, setApplications] = useState<ApplicationRowState[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [uploadingKeys, setUploadingKeys] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const autoSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setApplications([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getRenewalStatusDocuments(leadId)
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load Status documents data"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
      autoSaveTimers.current = {};
    };
  }, []);

  const totalCount = applications.length;
  const completedCount = useMemo(
    () =>
      applications.filter(
        (app) =>
          app.isRenewalIntimationUploaded && app.isStatusOfApplicationUploaded
      ).length,
    [applications]
  );

  const scheduleDateAutoSave = (
    applicationId: string,
    isoDate: string | null
  ) => {
    if (!leadId) return;
    const key = `date-${applicationId}`;
    const existing = autoSaveTimers.current[key];
    if (existing) clearTimeout(existing);
    autoSaveTimers.current[key] = setTimeout(() => {
      saveRenewalStatusDocuments(leadId, {
        applications: [
          {
            applicationId,
            validityDate1stRenewal: isoDate,
          },
        ],
      })
        .then((data) => {
          setApplications((prev) =>
            mergeApplications(prev, data.applications)
          );
        })
        .catch(() => {
          // Non-blocking; final submit surfaces real issues.
        });
    }, 600);
  };

  const handleRenewalDateChange = (index: number, next: Date | null) => {
    setApplications((prev) => {
      const arr = [...prev];
      const row = arr[index];
      if (!row) return prev;
      const iso = next ? toLocalYmd(next) : null;
      arr[index] = { ...row, validityDate1stRenewal: iso };
      scheduleDateAutoSave(row.applicationId, iso);
      return arr;
    });
  };

  const setRowUploading = (key: string, value: boolean) => {
    setUploadingKeys((prev) => {
      if (value) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const uploadFileForRow = async (
    index: number,
    slot: PdfSlot,
    file: File
  ) => {
    if (!leadId) return;
    const row = applications[index];
    if (!row) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    const applicationId = row.applicationId;
    const slotKey = `${applicationId}-${slot}`;

    setApplications((prev) => {
      const arr = [...prev];
      const target = arr.findIndex((a) => a.applicationId === applicationId);
      if (target === -1) return prev;
      arr[target] = {
        ...arr[target],
        ...(slot === "renewalIntimation"
          ? { pendingRenewalIntimationFilename: file.name }
          : { pendingStatusOfApplicationFilename: file.name }),
      };
      return arr;
    });
    setRowUploading(slotKey, true);

    try {
      const [presigned] = await generateUploadPresignedUrls(
        [
          {
            filename: file.name,
            mimeType: file.type || "application/pdf",
          },
        ],
        "private",
        `${UPLOAD_FOLDER_PREFIX}/${leadId}`
      );
      if (!presigned?.url || !presigned?.key) {
        throw new Error("Failed to generate upload URL");
      }

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/pdf",
        },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const data = await saveRenewalStatusDocuments(leadId, {
        applications: [
          slot === "renewalIntimation"
            ? {
                applicationId,
                renewalIntimationPdfFileKey: presigned.key,
                renewalIntimationPdfFilename: file.name,
              }
            : {
                applicationId,
                statusOfApplicationPdfFileKey: presigned.key,
                statusOfApplicationPdfFilename: file.name,
              },
        ],
      });

      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      setApplications((prev) =>
        prev.map((app) =>
          app.applicationId === applicationId
            ? {
                ...app,
                ...(slot === "renewalIntimation"
                  ? { pendingRenewalIntimationFilename: null }
                  : { pendingStatusOfApplicationFilename: null }),
              }
            : app
        )
      );
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setRowUploading(slotKey, false);
    }
  };

  const removeUploadedPdf = async (index: number, slot: PdfSlot) => {
    if (!leadId) return;
    const row = applications[index];
    if (!row) return;
    const fileKey =
      slot === "renewalIntimation"
        ? row.renewalIntimationPdfFileKey
        : row.statusOfApplicationPdfFileKey;
    if (!fileKey) return;
    try {
      const data = await saveRenewalStatusDocuments(leadId, {
        applications: [
          slot === "renewalIntimation"
            ? {
                applicationId: row.applicationId,
                renewalIntimationPdfFileKey: null,
                renewalIntimationPdfFilename: null,
              }
            : {
                applicationId: row.applicationId,
                statusOfApplicationPdfFileKey: null,
                statusOfApplicationPdfFilename: null,
              },
        ],
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File removed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove file");
    }
  };

  const openPreview = (row: ApplicationRowState, slot: PdfSlot) => {
    const url =
      slot === "renewalIntimation"
        ? row.renewalIntimationDownloadUrl
        : row.statusOfApplicationDownloadUrl;
    const name =
      slot === "renewalIntimation"
        ? row.renewalIntimationPdfFilename
        : row.statusOfApplicationPdfFilename;
    if (!url || !name) {
      toast.error("Preview is not available yet.");
      return;
    }
    setPreview({
      url,
      name,
      kind: detectPreviewKind(name),
    });
  };

  const closePreview = () => setPreview(null);

  const handleDownload = async (row: ApplicationRowState, slot: PdfSlot) => {
    const url =
      slot === "renewalIntimation"
        ? row.renewalIntimationDownloadUrl
        : row.statusOfApplicationDownloadUrl;
    const name =
      slot === "renewalIntimation"
        ? row.renewalIntimationPdfFilename
        : row.statusOfApplicationPdfFilename;
    if (!url || !name) {
      toast.error("No file available to download.");
      return;
    }
    try {
      const result = await downloadFileFromUrlWithFallback(url, name);
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info(
          "Opened the file in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download file");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (applications.length === 0) {
      toast.error("No applications found. Complete the previous steps first.");
      return;
    }
    Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
    autoSaveTimers.current = {};
    setSaving(true);
    try {
      const data = await saveRenewalStatusDocuments(leadId, {
        applications: applications.map((app) => ({
          applicationId: app.applicationId,
          validityDate1stRenewal: toIsoDate(app.validityDate1stRenewal),
        })),
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("Status documents saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading Status documents...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications found. Please complete the previous steps first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={RENEWAL_STATUS_DOCUMENTS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {applications.length > 1 && (
        <p
          className="text-xs font-medium leading-5 text-[#222]"
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        >
          {completedCount} of {totalCount} application{totalCount === 1 ? "" : "s"}{" "}
          with both PDFs uploaded.
        </p>
      )}

      {applications.map((row, idx) => {
        return (
          <div
            key={row.applicationId}
            className="flex flex-col gap-4 md:gap-5 w-full"
          >
            <div className="flex items-center gap-2 text-xs font-semibold leading-5 text-[#222]">
              <span>TM No.</span>
              <span className="text-[#101828]">{row.tmNo || "—"}</span>
              {row.mark ? (
                <span className="text-[#6D6D6D] font-normal">
                  · {row.mark}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-5">
              <div className="flex flex-1 min-w-0 flex-col w-full gap-1.5">
                <label className={LABEL_STYLE} style={LABEL_FEATURES}>
                  <span className="min-w-0 break-words">
                    Validity date of Registration
                  </span>
                </label>
                <DateTimePicker
                  value={dateValue(row.validityDate)}
                  readOnly
                  showTime={false}
                  buttonClassName="min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs bg-[#F7F7F7]"
                />
              </div>

              <div className="flex flex-1 min-w-0 flex-col w-full gap-1.5">
                <label className={LABEL_STYLE} style={LABEL_FEATURES}>
                  <span className="min-w-0 break-words">
                    Validity date of 1st Renewal of Registration
                  </span>
                </label>
                <DateTimePicker
                  value={dateValue(row.validityDate1stRenewal)}
                  onChange={(d) => handleRenewalDateChange(idx, d)}
                  showTime={false}
                  buttonClassName="min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-5 self-stretch">
            {(["renewalIntimation", "statusOfApplication"] as PdfSlot[]).map(
              (slot) => {
                const isRenewal = slot === "renewalIntimation";
                const slotKey = `${row.applicationId}-${slot}`;
                const inputId = `renewal-status-input-${slotKey}`;
                const uploading = !!uploadingKeys[slotKey];
                const dragging = draggingKey === slotKey;
                const filename = isRenewal
                  ? row.renewalIntimationPdfFilename
                  : row.statusOfApplicationPdfFilename;
                const pending = isRenewal
                  ? row.pendingRenewalIntimationFilename
                  : row.pendingStatusOfApplicationFilename;
                const downloadUrl = isRenewal
                  ? row.renewalIntimationDownloadUrl
                  : row.statusOfApplicationDownloadUrl;
                const fileLabel = pending ?? filename ?? null;
                const hasFile = !!fileLabel;
                const title = isRenewal
                  ? "Renewal Intimation PDF"
                  : "Status of Application PDF";
                const helpText = isRenewal
                  ? "Upload the Renewal Intimation PDF downloaded from the Trademarks Registry."
                  : "Upload the Status of Application PDF downloaded from the Trademarks Registry.";

                return (
                  <div
                    key={slot}
                    className="flex flex-1 min-w-0 flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <div className="flex flex-col gap-2 self-stretch">
                      <p
                        className="font-semibold text-[#101111]"
                        style={{
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0.01em",
                        }}
                      >
                        {title}
                      </p>
                      <p
                        className="font-normal text-[#7D7D7D]"
                        style={{
                          fontSize: "12px",
                          lineHeight: "24px",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {helpText}
                      </p>
                    </div>

                    <div
                      className={`self-stretch flex flex-col sm:flex-row sm:justify-between sm:items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
                        dragging
                          ? "border-[#222222] bg-[#F5F5F5]"
                          : hasFile
                            ? "border-[#22C55E] bg-[#F0FDF4]"
                            : "border-[#D8DAEB]"
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDraggingKey(slotKey);
                      }}
                      onDragLeave={() => setDraggingKey(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDraggingKey(null);
                        if (uploading) return;
                        const file = e.dataTransfer.files?.[0];
                        if (file) void uploadFileForRow(idx, slot, file);
                      }}
                      onClick={() => {
                        if (uploading) return;
                        document.getElementById(inputId)?.click();
                      }}
                    >
                      <div className="flex flex-row items-center gap-5 min-w-0">
                        <img
                          src={uploadDropzoneGraphic}
                          alt=""
                          width={60}
                          height={60}
                          className="flex-shrink-0"
                        />
                        <div className="flex flex-col gap-3 min-w-0">
                          {hasFile ? (
                            <>
                              <p
                                className="font-medium text-[#16A34A] truncate"
                                style={{
                                  fontSize: "16px",
                                  lineHeight: "100%",
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {fileLabel}
                              </p>
                              <p
                                className="font-normal text-[#7D7D7D]"
                                style={{ fontSize: "14px", lineHeight: "21px" }}
                              >
                                {uploading ? "Uploading…" : ACCEPTED_HELP}
                              </p>
                            </>
                          ) : (
                            <>
                              <p
                                className="font-medium text-[#101111]"
                                style={{
                                  fontSize: "16px",
                                  lineHeight: "100%",
                                  letterSpacing: "0.01em",
                                }}
                              >
                                Drag and drop your files
                              </p>
                              <p
                                className="font-normal text-[#7D7D7D]"
                                style={{ fontSize: "14px", lineHeight: "21px" }}
                              >
                                {uploading ? "Uploading…" : ACCEPTED_HELP}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={uploading}
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById(inputId)?.click();
                        }}
                        className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed self-start sm:self-auto"
                        style={{
                          boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "16px",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {hasFile ? "Replace" : "Select File"}
                      </button>
                    </div>

                    <input
                      id={inputId}
                      type="file"
                      accept={ACCEPTED_TYPES}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadFileForRow(idx, slot, file);
                        e.currentTarget.value = "";
                      }}
                    />

                    {filename && downloadUrl && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
                        <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                          {filename}
                        </span>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => openPreview(row, slot)}
                            className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDownload(row, slot)}
                            className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${filename}`}
                            onClick={() => void removeUploadedPdf(idx, slot)}
                            className="text-[#6D6D6D] hover:text-[#222] p-1"
                            title="Remove"
                          >
                            <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            )}
            </div>
          </div>
        );
      })}

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function mergeApplications(
  prev: ApplicationRowState[],
  next: RenewalStatusApplicationItem[]
): ApplicationRowState[] {
  const prevById = new Map(prev.map((a) => [a.applicationId, a]));
  return next.map((app) => {
    const cached = prevById.get(app.applicationId);
    return {
      ...app,
      pendingRenewalIntimationFilename:
        cached?.pendingRenewalIntimationFilename ?? null,
      pendingStatusOfApplicationFilename:
        cached?.pendingStatusOfApplicationFilename ?? null,
    };
  });
}
